<script setup>
import { ref, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { infoBoxIdEnum } from '@/stores/portal.store'

import InfoBox from '@/components/shared/InfoBox.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  // Suggested group name pre-filled in the input (e.g. the listing item name).
  defaultName: {
    type: String,
    default: '',
  },
  // Owned by the parent, which performs the async group + listing creation.
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const i18n = getI18n()

const name = ref(props.defaultName)

// Re-seed the input each time the dialog is (re)opened.
watch(
  () => props.modelValue,
  (open) => {
    if (open) name.value = props.defaultName
  },
)

const onCancel = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

const onConfirm = () => {
  if (!name.value?.trim()) return
  emit('confirm', name.value.trim())
}
</script>

<template>
  <q-dialog
    :model-value="modelValue"
    persistent
    class="dialog-xxs"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <q-card>
      <q-card-section>
        <div class="text-h6">{{ i18n.t('common.createServiceGroup') }}</div>
      </q-card-section>

      <q-card-section>
        <info-box
          :id="infoBoxIdEnum.SERVICE_GROUP_INFO"
          :message="i18n.t('common.serviceGroupInfoBanner')"
          class="q-mb-md"
        />

        <q-input
          v-model="name"
          :label="i18n.t('common.serviceGroupName')"
          autofocus
          outlined
          dense
          :rules="[val => !!(val && val.trim()) || i18n.t('common.required')]"
          @keyup.enter="onConfirm"
        />
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          flat
          :label="i18n.t('common.cancel')"
          @click="onCancel"
        />
        <q-btn
          flat
          color="primary"
          :label="i18n.t('common.confirm')"
          :disable="!name || !name.trim()"
          :loading="loading"
          @click="onConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
